/**
 * EnterpriseDashboard — portfolio + team activity layout.
 * Built on the same data feeds as PaidDashboard, plus:
 *   - /api/organization/portfolio      per-project latest snapshot
 *   - /api/organization/activity       combined audit + report feed
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { UploadZone } from '../components/UploadZone.jsx';
import { UsageMeter } from '../components/UsageMeter.jsx';
import { ScoreSparkline } from '../components/ScoreSparkline.jsx';
import { formatOverallScore } from '../../shared/scoreDisplay.js';
import { formatDateDDMonYYYY } from '../../utils/dates.js';
import { verdictClass } from '../utils/severity.js';

export function EnterpriseDashboard({ user, organization, navigate }) {
  const [usage, setUsage] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [trends, setTrends] = useState([]);
  const [activity, setActivity] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getOrgUsage(),
      api.getPortfolio(),
      api.getScoreTrends(),
      api.getActivity(),
    ])
      .then(([u, p, t, a]) => {
        if (cancelled) return;
        setUsage(u);
        setPortfolio(Array.isArray(p?.projects) ? p.projects : []);
        setTrends(Array.isArray(t?.projects) ? t.projects : []);
        setActivity(Array.isArray(a?.events) ? a.events : []);
      })
      .catch((e) => { if (!cancelled) setErr(e); });
    return () => { cancelled = true; };
  }, []);

  const isAdmin = user?.role === 'admin';
  const subStatus = organization?.subscription_status;
  const isReadOnlyAdmin =
    isAdmin && (subStatus === 'expired' || subStatus === 'read_only');
  const trendMap = new Map(trends.map((t) => [t.project_name, t.points]));

  return (
    <main className="main-content dashboard-enterprise" role="main">
      <section className="dashboard-hero">
        <h1>
          {organization?.name || 'Your organisation'}
        </h1>
        <p>
          Enterprise portfolio · welcome back{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
        </p>
      </section>

      {err && (
        <p className="recent-reports-error" role="alert">
          Couldn&rsquo;t load enterprise dashboard data. Try refreshing.
        </p>
      )}

      {usage && (
        <section className="dashboard-usage-grid" aria-label="Plan usage">
          <UsageMeter label="Projects" used={usage.projects_count} cap={usage.projects_cap} />
          <UsageMeter
            label="Team seats"
            used={usage.seat_count + usage.pending_invites_count}
            cap={usage.seat_cap}
          />
          <UsageMeter label="Reports (lifetime)" used={usage.reports_count} cap={null} />
          {isAdmin && (
            <>
              <a
                href="/team"
                className="dashboard-link-card"
                aria-disabled={isReadOnlyAdmin || undefined}
                aria-describedby={isReadOnlyAdmin ? 'manage-team-disabled-reason' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  if (isReadOnlyAdmin) return;
                  navigate('/team');
                }}
              >
                Manage team →
              </a>
              {isReadOnlyAdmin && (
                <span id="manage-team-disabled-reason" className="sr-only">
                  Read-only mode. Renew to manage team.
                </span>
              )}
            </>
          )}
        </section>
      )}

      <section className="dashboard-upload-inline">
        <h2>Upload a new schedule</h2>
        <UploadZone
          variant="compact"
          onUploaded={(d) => navigate(`/reports/${d.report_id}`)}
          disabled={isReadOnlyAdmin}
          disabledReason={isReadOnlyAdmin ? 'Read-only mode. Renew to upload schedules.' : null}
        />
      </section>

      <section className="portfolio-grid-section">
        <h2>Portfolio</h2>
        {portfolio.length === 0 ? (
          <p className="recent-reports-empty">No projects yet.</p>
        ) : (
          <div className="portfolio-grid">
            {portfolio.map((proj) => {
              const points = trendMap.get(proj.project_name) || [];
              const rejected = proj.latest_verdict === 'REJECTED';
              // F3 (Rule 12): colour the card + chip via the shared verdict →
              // severity mapping the health page uses — the badge TEXT already
              // reads the stored verdict verbatim.
              const vClass = verdictClass(proj.latest_verdict);
              return (
                <button
                  key={proj.project_name}
                  type="button"
                  className={`portfolio-card verdict-${vClass}`}
                  onClick={() => {
                    if (proj.latest_report_id) navigate(`/reports/${proj.latest_report_id}`);
                  }}
                >
                  <div className="portfolio-card-head">
                    <span className="portfolio-card-name">{proj.project_name}</span>
                    <span className={`verdict-chip verdict-${vClass}`}>
                      {proj.latest_verdict || '—'}
                    </span>
                  </div>
                  <div className="portfolio-card-score">
                    {/* FIX-B Part 3: the ONE display score (shared formatter). */}
                    {formatOverallScore(proj.latest_score)}
                  </div>
                  <ScoreSparkline points={points} rejected={rejected} />
                  <div className="portfolio-card-meta">
                    <span>{proj.report_count} report{proj.report_count === 1 ? '' : 's'}</span>
                    <span>{formatDate(proj.latest_created_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="activity-feed-section">
        <h2>Team activity</h2>
        {activity.length === 0 ? (
          <p className="recent-reports-empty">No activity yet.</p>
        ) : (
          <ul className="activity-feed">
            {activity.map((ev, i) => (
              <li key={`${ev.kind}-${ev.created_at}-${i}`} className={`activity-item activity-${ev.kind}`}>
                <span className="activity-when">{formatRelative(ev.created_at)}</span>
                <span className="activity-text">{formatActivity(ev)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

// A1 (D-ListDateFormat): route through the one shared DD Mon YYYY formatter.
function formatDate(iso) {
  return formatDateDDMonYYYY(iso);
}

function formatRelative(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return formatDate(iso);
}

function formatActivity(ev) {
  const actor = ev.actor_name || ev.actor_email || 'Someone';
  switch (ev.kind) {
    case 'report_uploaded':
      return `${actor} uploaded ${ev.data?.project_name || 'a report'} (score ${
        formatOverallScore(ev.data?.overall_score)
      })`;
    case 'invite_sent':
      return `${actor} invited ${ev.data?.invited_email || 'a teammate'}`;
    case 'invite_accepted':
      return `${actor} accepted their invitation`;
    case 'invite_revoked':
      return `${actor} revoked an invitation`;
    case 'member_removed':
      return `${actor} removed a member`;
    case 'login_succeeded':
      return `${actor} signed in`;
    default:
      return `${actor} · ${ev.kind}`;
  }
}
